import React from 'react';
import { Database, Users, Zap } from 'lucide-react';

const AIPlatformInfographic = () => {
  return (
    <section id="ai-platform" className="section-padding bg-black text-white py-20">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4">
            Your In-House <span className="text-[#2aa996]">Artificial Intelligence</span> Platform
          </h2>
          <p className="text-xl text-gray-400">
            Your Data, Your Context, Your IP
          </p>
        </div>

        {/* Three Component Diagram - Triangular Layout */}
        <div className="relative max-w-5xl mx-auto hidden md:block">
          {/* Top Left - AI Engine */}
          <div className="absolute top-0 left-0 lg:left-1/4 text-center" style={{ transform: 'translateX(-50%)' }}>
            {/* Concentric Circles Icon */}
            <div className="relative mb-4">
              <svg width="120" height="120" className="mx-auto">
                {/* Outer teal circle */}
                <circle cx="60" cy="60" r="55" fill="#2aa996" opacity="0.3" />
                {/* Middle teal circle */}
                <circle cx="60" cy="60" r="40" fill="#2aa996" opacity="0.5" />
                {/* Inner purple circle */}
                <circle cx="60" cy="60" r="25" fill="#9333ea" opacity="0.7" />
                {/* Center dark purple */}
                <circle cx="60" cy="60" r="15" fill="#6b21a8" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2 text-gray-200">Agnostic AI Engine</h3>
            <p className="text-sm text-gray-400">Your AI Toolbox</p>
          </div>

          {/* Top Right - Team */}
          <div className="absolute top-0 right-0 lg:right-1/4 text-center" style={{ transform: 'translateX(50%)' }}>
            {/* Three People Icon */}
            <div className="relative mb-4">
              <svg width="120" height="120" className="mx-auto">
                {/* Person 1 */}
                <circle cx="30" cy="30" r="12" fill="none" stroke="#9333ea" strokeWidth="3" />
                <rect x="22" y="42" width="16" height="20" fill="none" stroke="#9333ea" strokeWidth="3" rx="2" />
                {/* Person 2 */}
                <circle cx="60" cy="30" r="12" fill="none" stroke="#9333ea" strokeWidth="3" />
                <rect x="52" y="42" width="16" height="20" fill="none" stroke="#9333ea" strokeWidth="3" rx="2" />
                {/* Person 3 */}
                <circle cx="90" cy="30" r="12" fill="none" stroke="#9333ea" strokeWidth="3" />
                <rect x="82" y="42" width="16" height="20" fill="none" stroke="#9333ea" strokeWidth="3" rx="2" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2 text-gray-200">Expert Team</h3>
            <p className="text-sm text-gray-400">Your dedicated AI Team</p>
          </div>

          {/* Bottom Center - Your Domain */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-center" style={{ top: '280px' }}>
            {/* Corner Brackets Icon */}
            <div className="relative mb-4">
              <svg width="120" height="120" className="mx-auto">
                {/* Top-left bracket */}
                <path d="M 30 30 L 30 50 L 50 50" fill="none" stroke="#9333ea" strokeWidth="4" strokeLinecap="round" />
                {/* Top-right bracket */}
                <path d="M 90 30 L 90 50 L 70 50" fill="none" stroke="#9333ea" strokeWidth="4" strokeLinecap="round" />
                {/* Bottom-left bracket */}
                <path d="M 30 90 L 30 70 L 50 70" fill="none" stroke="#9333ea" strokeWidth="4" strokeLinecap="round" />
                {/* Bottom-right bracket */}
                <path d="M 90 90 L 90 70 L 70 70" fill="none" stroke="#9333ea" strokeWidth="4" strokeLinecap="round" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2 text-gray-200">Your Domain</h3>
            <p className="text-sm text-gray-400">Your data, your context</p>
          </div>

          {/* Connection Lines */}
          <svg className="absolute inset-0 w-full h-full" style={{ top: '60px', height: '320px' }}>
            {/* Line from AI Engine to Domain */}
            <line x1="25%" y1="120" x2="50%" y2="280" stroke="#2aa996" strokeWidth="2" strokeDasharray="5,5" opacity="0.5" />
            {/* Line from Team to Domain */}
            <line x1="75%" y1="120" x2="50%" y2="280" stroke="#9333ea" strokeWidth="2" strokeDasharray="5,5" opacity="0.5" />
            {/* Horizontal line connecting AI Engine and Team */}
            <line x1="25%" y1="120" x2="75%" y2="120" stroke="#2aa996" strokeWidth="2" strokeDasharray="5,5" opacity="0.3" />
          </svg>
        </div>

        {/* Spacer for absolute positioning */}
        <div className="h-96"></div>

        {/* Comparison Section */}
        <div className="mt-32 max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-12">
            Agnostic AI Engine <span className="text-[#2aa996]">vs.</span> Off-the-Shelf "SaaS AI"
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Agnostic AI */}
            <div className="bg-gradient-to-br from-[#2aa996]/20 to-purple-600/20 rounded-xl p-8 border-2 border-[#2aa996]">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-16 h-16 bg-[#2aa996] rounded-lg flex items-center justify-center">
                  <Zap className="text-white" size={32} />
                </div>
                <h4 className="text-2xl font-bold">Agnostic AI Engine</h4>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#2aa996] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="font-semibold">Your Infrastructure</div>
                    <div className="text-gray-400 text-sm">Deployed on your servers, your cloud, your control</div>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#2aa996] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="font-semibold">Domain-Specific</div>
                    <div className="text-gray-400 text-sm">Fine-tuned for your industry, your processes, your data</div>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#2aa996] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="font-semibold">No Vendor Lock-In</div>
                    <div className="text-gray-400 text-sm">Works with any LLM, switch providers anytime</div>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#2aa996] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="font-semibold">Your IP, Your Data</div>
                    <div className="text-gray-400 text-sm">Complete ownership, no data sharing, full privacy</div>
                  </div>
                </li>
              </ul>
            </div>

            {/* SaaS AI */}
            <div className="bg-gray-800/50 rounded-xl p-8 border-2 border-gray-700">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-16 h-16 bg-gray-600 rounded-lg flex items-center justify-center">
                  <Database className="text-white" size={32} />
                </div>
                <h4 className="text-2xl font-bold text-gray-400">Off-the-Shelf SaaS AI</h4>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-gray-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="font-semibold text-gray-400">Their Infrastructure</div>
                    <div className="text-gray-500 text-sm">Hosted on their servers, limited control</div>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-gray-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="font-semibold text-gray-400">Generic Solution</div>
                    <div className="text-gray-500 text-sm">One-size-fits-all, not tailored to your needs</div>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-gray-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="font-semibold text-gray-400">Vendor Lock-In</div>
                    <div className="text-gray-500 text-sm">Tied to one provider, difficult to switch</div>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-gray-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="font-semibold text-gray-400">Data Sharing</div>
                    <div className="text-gray-500 text-sm">Your data may be used for training, privacy concerns</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Text */}
        <div className="text-center mt-16">
          <p className="text-lg text-gray-400">
            Safely deployed, domain-specific, fine-tuned AI for business.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AIPlatformInfographic;


